import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../Pipeline/Pipeline.module.scss";
import { useGetKpiSettingsQuery } from "../../../../redux/services/kpiSettingsByTypeAPI";
import { useProfileParams } from "../../../../utils/common";
import InsightAI from "../../../common/InsightAI/InsightAI";
import InteractiveTileGrid from "../InteractiveTileGrid/InteractiveTileGrid";
import { useGetBusinessPerformanceSummaryQuery } from "../../../../redux/services/businessPerformaceAPI";
import { useGetAGCAIDetailsMutation } from "../../../../redux/services/agcAIAPI";
import isEmpty from "lodash/isEmpty";
import { normalizeMetricSourceData } from "../../../../utils/agcUtil";
import { Revenue, RevenueDetails } from "./KPI/Revenue/Revenue";
import BusinessLine, {
  BusinessLineDetails,
} from "./KPI/BusinessLine/BusinessLine";
import CategoryMix, {
  CategoryMixDetails,
} from "./KPI/CategoryMix/CategoryMix";
import SubCategory, { SubCategoryDetails } from "./KPI/SubCategory/SubCategory";
import TopProducts, { TopProductsDetails } from "./KPI/TopProducts/TopProducts";

interface CategoriesProps {
  category?: string;
}

const Portfolio: React.FC<CategoriesProps> = () => {
  const { t } = useTranslation();
  const [portfolioKPIsCodelist, setPortfolioKPIsCodelist] = useState<
    { code: string }[]
  >([]);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [aiContent, setAiContent] = useState<string>("");
  const hasRequestedRef = useRef(false);

  const { data: portfolioKpisResponse, isFetching: isFetchingKpiSettings } =
    useGetKpiSettingsQuery(
    {
      type: "business-performance-portfolio",
      affiliateCode,
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: performanceSummaryResponse,
    isLoading: isLoadingSummary,
  } = useGetBusinessPerformanceSummaryQuery(
    { affiliateCode, aboNumber, payload: { kpis: portfolioKPIsCodelist } },
    {
      refetchOnMountOrArgChange: true,
      skip:
        Number(affiliateCode) === 0 ||
        aboNumber === 0 ||
        isEmpty(portfolioKPIsCodelist),
    }
  );

  const [getAGCAIDetails, { data: aiDetailsResp }] =
    useGetAGCAIDetailsMutation();

  const loading = isLoadingSummary || isFetchingKpiSettings;

  useEffect(() => {
    const response = portfolioKpisResponse?.body || [];
    if (!isEmpty(response)) {
      const kpiCodes = response.map((item: { code: string }) => ({
        code: item.code,
      }));
      setPortfolioKPIsCodelist(kpiCodes);
    }
  }, [portfolioKpisResponse]);

  // Fetch AI summary for portfolio category
  useEffect(() => {
    const canFetch =
      Number(affiliateCode) !== 0 &&
      aboNumber !== 0 &&
      !isEmpty(portfolioKPIsCodelist);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({
      affiliateCode,
      aboNumber,
      payload: { kpis: portfolioKPIsCodelist } as any,
    });
  }, [affiliateCode, aboNumber, portfolioKPIsCodelist, getAGCAIDetails]);

  // Extract AI content from response
  useEffect(() => {
    if (aiDetailsResp?.body) {
      const aiSummary = aiDetailsResp.body?.summary || "";
      setAiContent(aiSummary);
    }
  }, [aiDetailsResp]);

  const kpisData = performanceSummaryResponse?.body?.kpis || [];
  const summaryByCode = (code: string) => kpisData.find((k: any) => k.kpiCode === code);

  // Extract individual KPI data from summary response
  const revenueSummary = summaryByCode("revenue");

  const businessLineKpi = summaryByCode("business_line");
  const businessLineSummary = normalizeMetricSourceData(businessLineKpi);

  const categoryMixKpi = summaryByCode("category");
  const categoryMixSummary = normalizeMetricSourceData(categoryMixKpi);

  const subCategoryKpi = summaryByCode("sub_category");
  const subCategorySummary = normalizeMetricSourceData(subCategoryKpi);

  const topProductsKpi = summaryByCode("top10Product");
  const topProductsSummary = normalizeMetricSourceData(topProductsKpi);

  return (
    <div className={styles.pipeline_wrapper}>
      <div className={styles.heading_area}>
        <div className={styles.heading}>{t("portfolio_performance")}</div>
      </div>
      <InsightAI
        content={aiContent}
        className={styles.insight_ai}
        showDropdown={true}
        updateDate="08/19/2024"
        isDropdownEnabled={true}
        flowerIconSize={24}
        title={"ai_summary_portfolio"}
      />
      <InteractiveTileGrid
        className={styles.tiles_grid_container}
        tiles={[
          {
            id: "revenue",
            kpiTile: <Revenue summary={revenueSummary} loading={loading} />,
            kpiDetailTile: <RevenueDetails />,
          },
          {
            id: "business_line",
            kpiTile: (
              <BusinessLine summary={businessLineSummary} loading={loading} />
            ),
            kpiDetailTile: <BusinessLineDetails kpiCode="business_line" />,
          },
          {
            id: "category",
            kpiTile: (
              <CategoryMix summary={categoryMixSummary} loading={loading} />
            ),
            kpiDetailTile: <CategoryMixDetails kpiCode="category" />,
          },
          {
            id: "sub_category",
            kpiTile: (
              <SubCategory summary={subCategorySummary} loading={loading} />
            ),
            kpiDetailTile: <SubCategoryDetails kpiCode="sub_category" />,
          },
          {
            id: "top10Product",
            kpiTile: (
              <TopProducts summary={topProductsSummary} loading={loading} />
            ),
            kpiDetailTile: <TopProductsDetails kpiCode="top10Product" />,
          },
        ]}
      />
    </div>
  );
};

export default Portfolio;
